/**
 * Расширение окна товара для пряников и наборов.
 *
 * Окно товара общее (`features/products-v2/components/ProductCardModal`), а
 * пряники дают ему своё: вид в шапке («Пряник», «Набор пряников · PP1»,
 * «Набор PP2»), звено «Задание генератора» в цепочке, вкладки «Состав» и
 * «Производство», размер и вес в колонке, проверки файлов и визуалов и кнопки
 * «Что дальше»: «Сделать визуалы» (нет слайдов) или «Открыть черновики», у PP1
 * ещё «Сцена на торте». Подключается свойством `extension` — страницей пряников,
 * каталогом товаров и навигатором окон, чтобы пряник выглядел одинаково везде.
 */

import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Boxes, Cookie, ExternalLink, Images, Sparkles } from 'lucide-react'
import { hasPermission } from '../../app/access'
import { useAuthStore } from '../../stores/authStore'
import type { NextStepAction } from '../../components/ui/NextStepBar'
import type { CardSidebarStat } from '../marketplace-catalog/components/CardSidebar'
import type { EntityMoreMenuItem } from '../../components/entity-view/EntityModalShell'
import { NO_DATA } from '../products-v2/components/product-window/model'
import {
  EMPTY_CONTRIBUTION,
  type ProductWindowContribution,
  type ProductWindowExtension,
} from '../products-v2/components/product-window/types'
import type { ProductV2 } from '../products-v2/types'
import { gingerbreadCatalogAPI } from './api'
import { CompositionTab } from './components/CompositionTab'
import { resolvePreview } from './components/GingerbreadThumb'
import { ProductionTab } from './components/ProductionTab'
import {
  fileFlags,
  filesComplete,
  formatArea,
  formatGrams,
  formatSheetShare,
  formatSize,
  isComputedWeight,
  isPp2Kit,
  jobHref,
} from './lib'
import { isGingerbreadArticle, isGingerbreadProduct, provisionalKind, useGingerbreadBlock } from './useGingerbreadBlock'
import VisualsCardsDialog from './visuals-cards/VisualsCardsDialog'
import { MARKETPLACE_HREF, type GingerbreadVisuals } from './visuals'
import type { GingerbreadBlock, GingerbreadRow } from './types'

const KIND_LABEL_BY_VARIANT = {
  single: 'Пряник',
  kit: 'Набор пряников · PP1',
  kit_pp2: 'Набор PP2',
} as const

/** Слайды, статус визуалов и черновики пряника; null — не грузим (нет права или не пряник). */
function useGingerbreadVisuals(article: string | null) {
  const [data, setData] = useState<GingerbreadVisuals | null>(null)
  const [error, setError] = useState(false)
  const [tick, setTick] = useState(0)
  const reload = useCallback(() => setTick((value) => value + 1), [])

  useEffect(() => {
    setData(null)
    setError(false)
    if (!article) return undefined
    let cancelled = false
    Promise.resolve()
      .then(() => gingerbreadCatalogAPI.getVisuals(article))
      .then((result) => {
        if (!cancelled) setData(result)
      })
      .catch(() => {
        if (!cancelled) setError(true)
      })
    return () => {
      cancelled = true
    }
  }, [article, tick])

  return { data, error, reload }
}

const noData = (label: string, note: string): CardSidebarStat => ({ label, value: NO_DATA, tone: 'dim', note })

function sidebarStats(block: GingerbreadBlock | null | undefined): CardSidebarStat[] {
  if (block === undefined) return [noData('Данные пряника', 'загружаются')]
  if (block === null) return [noData('Данные пряника', 'у товара нет данных генератора')]
  const computed = isComputedWeight(block)
  const weights: CardSidebarStat[] = [
    block.weightNetG != null
      ? { label: 'Вес нетто', value: formatGrams(block.weightNetG), note: computed ? 'расчётный' : undefined }
      : noData('Вес нетто', 'вес не задан'),
    block.weightGrossG != null
      ? { label: 'Вес брутто', value: formatGrams(block.weightGrossG), note: computed ? 'расчётный' : undefined }
      : noData('Вес брутто', 'вес не задан'),
  ]
  if (block.kind === 'kit') {
    const count = block.itemsCount ?? block.components.length
    return [
      count > 0 ? { label: 'Фигур в наборе', value: count } : noData('Фигур в наборе', 'состав не записан'),
      ...weights,
      block.itemsCount || block.components.length
        ? { label: 'Доля листа', value: formatSheetShare(block), note: 'на одну позицию' }
        : noData('Доля листа', 'состав не записан'),
    ]
  }
  return [
    block.widthMm != null && block.heightMm != null
      ? { label: 'Размер', value: formatSize(block) }
      : noData('Размер', 'контур не измерен'),
    ...weights,
    block.sugarAreaCm2 != null
      ? { label: 'Сахарная бумага', value: formatArea(block.sugarAreaCm2), note: 'площадь' }
      : noData('Сахарная бумага', 'площадь не посчитана'),
    { label: 'Входит в наборы', value: block.usedIn.length, note: block.usedIn.length ? undefined : 'пока не входит' },
  ]
}

function useContribution(
  product: ProductV2 | null,
  context: Parameters<ProductWindowExtension['useContribution']>[1],
): ProductWindowContribution {
  // Артикул окна известен до загрузки товара: блок генератора и визуалы грузятся параллельно товару.
  const hintArticle = context.article ?? null
  const enabled = isGingerbreadProduct(product) || (!product && isGingerbreadArticle(hintArticle))
  const { block, loading } = useGingerbreadBlock(product, enabled, hintArticle)
  const permissions = useAuthStore((state) => state.user?.permissions ?? NO_PERMISSIONS)
  const navigate = useNavigate()
  const canReadGingerbread = hasPermission('Gingerbread:READ', permissions)
  const canRunFunnel = hasPermission('ContentFunnel:UPDATE', permissions)
  const canApprove = hasPermission('Gingerbread:APPROVE', permissions)
  const article = enabled ? (product?.article ?? hintArticle) : null
  const visuals = useGingerbreadVisuals(article && canReadGingerbread ? article : null)
  const [visualsOpen, setVisualsOpen] = useState(false)

  if (!enabled || !product || !article) return EMPTY_CONTRIBUTION

  const variant = block ? (block.kind === 'single' ? 'single' : isPp2Kit(block) ? 'kit_pp2' : 'kit') : provisionalKind(product)
  const isKit = variant !== 'single'
  const pp2 = variant === 'kit_pp2'
  const row = { ...product, gingerbread: block === undefined ? (product as GingerbreadRow).gingerbread : block } as GingerbreadRow
  // Главное изображение колонки: превью листа из блока, а пока блок грузится (или его нет) —
  // та же лестница, что у вкладки «Производство» (превью файла печати, затем превью макета).
  const hero = resolvePreview(row)

  // ── Визуалы и черновики ──
  const slides = visuals.data?.slides.length ?? 0
  const visualsStatus = visuals.data?.visualsStatus ?? null
  const drafts = visuals.data?.drafts ?? []
  const draftMarketplaces = [...new Set(drafts.map((draft) => draft.marketplace).filter((value): value is NonNullable<typeof value> => value != null))]
  const hasVisuals = slides > 0 && visualsStatus !== 'failed'
  const draftsHref = draftMarketplaces[0] ? MARKETPLACE_HREF[draftMarketplaces[0]] : MARKETPLACE_HREF.wb

  const actions: NextStepAction[] = []
  if (canReadGingerbread && visuals.data === null && !visuals.error) {
    // данные визуалов ещё грузятся: главной кнопки пока нет, чтобы не предлагать лишнее
  } else if (!hasVisuals || visualsStatus === 'rendering') {
    actions.push({
      key: 'make-visuals',
      label: visualsStatus === 'rendering' ? 'Визуалы создаются' : 'Сделать визуалы',
      icon: <Images className="h-4 w-4" />,
      onClick: () => setVisualsOpen(true),
      disabledReason: canRunFunnel ? undefined : 'Нужно право ContentFunnel:UPDATE',
    })
  } else {
    actions.push({
      key: 'open-drafts',
      label: 'Открыть черновики',
      icon: <ExternalLink className="h-4 w-4" />,
      onClick: () => context.guard(() => navigate(draftsHref)),
    })
  }
  if (variant === 'kit') {
    actions.push({
      key: 'kit-scene',
      label: 'Сцена на торте',
      icon: <Sparkles className="h-4 w-4" />,
      onClick: () => {
        context.setTab('production')
        // вкладка рисуется следующим кадром: блок сцены появится после неё
        window.setTimeout(
          () => document.querySelector('[data-testid="kit-scene-block"]')?.scrollIntoView?.({ block: 'center' }),
          150,
        )
      },
      disabledReason: canApprove ? undefined : 'Нужно право Gingerbread:APPROVE',
    })
  }

  // ── Проверки ──
  const goProduction = () => context.setTab('production')
  const filesCheck: ProductWindowContribution['checks'] = [
    block === undefined
      ? { key: 'print', label: 'Файлы', tone: 'none', title: 'данные генератора загружаются', onClick: goProduction }
      : block === null
        ? { key: 'print', label: 'Файлы', tone: 'none', title: 'у товара нет данных генератора пряников', onClick: goProduction }
        : filesComplete(block)
          ? {
              key: 'print',
              label: 'Файлы',
              value: fileFlags(block).filter((flag) => flag.present).map((flag) => flag.label).join(' · '),
              tone: 'ok',
              onClick: goProduction,
            }
          : {
              key: 'print',
              label: 'Файлы',
              value: fileFlags(block).filter((flag) => !flag.present).map((flag) => flag.label).join(', ') + ' нет',
              tone: 'warn',
              onClick: goProduction,
              problem: 'Не хватает файлов производства',
            },
  ]
  const visualsCheck: NonNullable<ProductWindowContribution['checks']>[number] = !canReadGingerbread
    ? { key: 'visuals', label: 'Визуалы', tone: 'none', title: 'нужно право Gingerbread:READ', onClick: () => context.setTab('production') }
    : visuals.error
      ? { key: 'visuals', label: 'Визуалы', tone: 'none', title: 'визуалы не загрузились', onClick: () => context.setTab('production') }
      : visuals.data === null
        ? { key: 'visuals', label: 'Визуалы', tone: 'none', title: 'визуалы загружаются', onClick: () => context.setTab('production') }
        : visualsStatus === 'failed'
          ? { key: 'visuals', label: 'Визуалы', value: 'ошибка', tone: 'bad', onClick: () => context.setTab('production'), problem: 'Визуалы не созданы: ошибка генерации' }
          : visualsStatus === 'rendering'
            ? { key: 'visuals', label: 'Визуалы', value: 'создаются', tone: 'info', onClick: () => context.setTab('production') }
            : slides > 0
              ? { key: 'visuals', label: 'Визуалы', value: `${slides} слайд.`, tone: 'ok', onClick: () => context.setTab('production') }
              : { key: 'visuals', label: 'Визуалы', value: 'нет', tone: 'warn', onClick: () => context.setTab('production'), problem: 'Нет визуалов для карточек' }
  const checks = [...(filesCheck ?? []), visualsCheck]
  if (visuals.data && drafts.length > 0) {
    checks.push({
      key: 'drafts',
      label: 'Черновики',
      value: draftMarketplaces.map((marketplace) => ({ wb: 'ВБ', ozon: 'ОЗ', ym: 'ЯМ' })[marketplace]).join(', ') || drafts.length,
      tone: 'info',
      onClick: () => context.setTab('places'),
    })
  }
  if (block) {
    checks.push(
      isKit
        ? {
            key: 'composition',
            label: 'Состав',
            value: block.itemsCount ?? block.components.length,
            tone: (block.itemsCount ?? block.components.length) > 0 ? 'ok' : 'warn',
            onClick: () => context.setTab('composition'),
            ...((block.itemsCount ?? block.components.length) > 0 ? {} : { problem: 'Состав набора не записан' }),
          }
        : { key: 'in-kits', label: 'В наборах', value: block.usedIn.length, tone: block.usedIn.length > 0 ? 'ok' : 'info', onClick: () => context.setTab('composition') },
    )
  }

  const moreMenu: EntityMoreMenuItem[] = []
  if (block?.jobId && !pp2) {
    const jobId = block.jobId
    moreMenu.push({
      key: 'open-job',
      label: 'Открыть задание генератора',
      icon: <ExternalLink className="h-4 w-4" />,
      onClick: () => context.guard(() => navigate(jobHref(jobId))),
    })
  }

  return {
    kind: { label: KIND_LABEL_BY_VARIANT[variant], icon: isKit ? Boxes : Cookie },
    chainBefore:
      block?.jobId && !pp2
        ? [
            {
              key: 'job',
              label: 'Задание генератора',
              sub: block.jobName ?? undefined,
              tone: 'ok',
              title: 'Открыть задание генератора',
              onClick: () => context.guard(() => navigate(jobHref(block.jobId as string))),
            },
          ]
        : [],
    meta: [
      { label: 'Форма', value: block && !isKit ? block.shapeName : null },
      { label: 'Задание', value: block && !pp2 ? block.jobName : null },
    ],
    heroUrl: hero?.url ?? null,
    stats: sidebarStats(block),
    tabs: [
      {
        key: 'composition',
        label: 'Состав',
        count: block ? (isKit ? block.components.length : block.usedIn.length) || undefined : undefined,
        render: () => <CompositionTab block={block} loading={loading} />,
      },
      {
        key: 'production',
        label: 'Производство',
        attention: block && !filesComplete(block) ? 'warn' : undefined,
        render: (current) => (
          <ProductionTab product={block === undefined ? (current as GingerbreadRow) : ({ ...current, gingerbread: block } as GingerbreadRow)} />
        ),
      },
    ],
    actions,
    checks,
    hideActions: ['print', 'create-cards'],
    moreMenu,
    overlays: (
      <VisualsCardsDialog
        isOpen={visualsOpen}
        articles={[article]}
        onClose={() => {
          setVisualsOpen(false)
          visuals.reload()
        }}
      />
    ),
  }
}

// Стабильный литерал: селектор стора не должен считать пользователя без прав меняющимся на каждый рендер.
const NO_PERMISSIONS: string[] = []

/** Расширение окна товара для пряников и наборов: константа модуля, ссылка стабильна. */
export const gingerbreadProductWindow: ProductWindowExtension = { useContribution }
